import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
  useLayoutEffect
} from 'react'
import { eventBus, EVENTS } from '../../../../utils/EventBus'

import { useScrollPosition } from './hooks/useScrollPosition'
import { useContextMenu } from './hooks/useContextMenu'
import { useColumnResize } from './hooks/useColumnResize'
import { useHeaderFieldEditing } from './hooks/useHeaderFieldEditing'
import { useCustomerCodeRename } from './hooks/useCustomerCodeRename'
import { useColumnAddition } from './hooks/useColumnAddition'
import { useClipboard } from './hooks/useClipboard'
import { useColumnReorder } from './hooks/useColumnReorder'
import { useRowReorder } from './hooks/useRowReorder'

import EmptyState from './components/EmptyState'
import TableHeader from './components/TableHeader'
import TableBody from './components/TableBody'
import ContextMenuOverlay from './components/ContextMenuOverlay'

const fieldMap = {
  input_price: '仕入れ価格',
  output_price: '単価',
  quantity_unit: '入数',
  quantity: '数量',
  listing_quantity: '上場',
  shipping_mark: '荷印',
  grade: '等級',
  size: '階級'
}

const DistributionTable = ({
  selectedItems,
  allItems = selectedItems,
  hiddenRowKeys,
  hiddenColumnKeys,
  hiddenHeaderFieldKeys,
  onHideRow,
  onHideHeaderField,
  onHideColumn,
  onUnhideItem,
  distributionManager,
  columnManager,
  todayString,
  onAddNewRow,
  onAddPrice,
  onDeletePrice,
  orders,
  sellerCode,
  productName,
  saleMan,
  gradeOptions,
  sizeOptions,
  onRowReorder,
  onDeleteColumn,
  hasOriginalOrderQuantity,
  layoutUpdate,
  farmFilterValues = [],
  gradeFilterValues = [],
  sizeFilterValues = [],
  gradeFilterOptions = [],
  sizeFilterOptions = [],
  onGradeFilterChange,
  onSizeFilterChange,
  isFilterLoading = false,
  rowReorderDisabled = false,
  isReadOnly = false
}) => {
  const [showTooltip, setShowTooltip] = useState(null)
  const [newColumnId, setNewColumnId] = useState(null)
  const [editingColumnId, setEditingColumnId] = useState(null)

  const { tableScrollRef, captureScrollPosition, scrollToRight } =
    useScrollPosition()

  const {
    selectedRow,
    setSelectedRow,
    selectedRowIndexes,
    setSelectedRowIndexes,
    selectedColumn,
    setSelectedColumn,
    selectedColumnIds,
    setSelectedColumnIds,
    contextMenu,
    clearSelection,
    closeContextMenu,
    handleRowContextMenu,
    handleColumnContextMenu,
    handleHeaderRowContextMenu,
    handleRowSelection,
    handleColumnSelection,
    selectedHeaderFieldKeys,
    handleHeaderFieldSelection
  } = useContextMenu()

  const { handleMouseDown } = useColumnResize(columnManager)

  const {
    handleHeaderFieldBlur,
    handleHeaderFieldKeyDown,
    handleHeaderFieldKeyUp,
    handleHeaderFieldToggle
  } = useHeaderFieldEditing(columnManager, distributionManager)

  const {
    newColumnInputRefs,
    hasAutoFocused,
    handleCustomerCodeChange,
    handleCustomerCodeEmptyLabel
  } = useCustomerCodeRename(
    columnManager,
    distributionManager,
    setNewColumnId,
    sellerCode
  )

  const { isDisabled, handleAddNewColumn } = useColumnAddition(
    columnManager,
    distributionManager,
    setNewColumnId,
    scrollToRight,
    sellerCode,
    allItems
  )

  const {
    clipboardInfo,
    handleCopyRow,
    handlePasteRow,
    handleCopyColumn,
    handlePasteColumn,
    handleInsertColumnLeft,
    handleInsertColumnRight,
    handleDeleteColumn,
    handleHideRow,
    handleHideHeaderField,
    handleHideColumn,
    clearClipboard
  } = useClipboard({
    distributionManager,
    columnManager,
    selectedItems: allItems,
    selectedRow,
    selectedColumn,
    setSelectedColumn,
    setNewColumnId,
    scrollToRight,
    contextMenu,
    closeContextMenu,
    onDeleteColumn,
    onHideRow,
    onHideHeaderField,
    onHideColumn
  })

  const {
    handleDragStart: baseHandleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    draggedColumnId,
    dropTargetId,
    isAtEnd
  } = useColumnReorder(columnManager, selectedColumnIds, setSelectedColumnIds)

  const handleDragStart = useCallback(
    (e, columnId) => {
      setSelectedRowIndexes([])
      setSelectedRow(null)
      baseHandleDragStart(e, columnId)
    },
    [setSelectedRowIndexes, setSelectedRow, baseHandleDragStart]
  )

  const {
    handleRowDragStart: baseHandleRowDragStart,
    handleRowDragEnd,
    handleRowDragOver,
    handleRowDrop,
    draggedRowIdx,
    draggedRowKey,
    dropTargetRowIdx
  } = useRowReorder(selectedRowIndexes, onRowReorder, setSelectedRowIndexes)

  const handleRowDragStart = useCallback(
    (e, rowIdx) => {
      setSelectedColumnIds([])
      setSelectedColumn(null)
      baseHandleRowDragStart(e, rowIdx)
    },
    [setSelectedColumnIds, setSelectedColumn, baseHandleRowDragStart]
  )

  const handleFixedCellChange = useCallback(
    (item, field, value) => {
      if (isReadOnly) return
      const hinmeiCode = item.hinmeiCode || ''
      const niukabango = item.niukabango
      const rowKey = distributionManager.getRowKey(hinmeiCode, niukabango)
      const fixedFieldKey = `__fixed_${field}`

      const allowZeroFixedFields = new Set([
        'quantity',
        'listing_quantity',
        'input_price',
        'output_price'
      ])
      const normalizeFixed = (v, targetField) => {
        if (v === undefined || v === null) return ''
        if (targetField === 'grade' || targetField === 'size') return String(v)
        if (!allowZeroFixedFields.has(targetField) && Number(v) === 0) return ''
        return v
      }

      const hadFixedField =
        fixedFieldKey in (distributionManager.data[rowKey] ?? {})
      const rawBefore = hadFixedField
        ? distributionManager.data[rowKey][fixedFieldKey]
        : item[field]

      const beforeValue =
        !hadFixedField &&
        ['listing_quantity', 'input_price', 'output_price'].includes(field) &&
        Number(rawBefore) === 0
          ? ''
          : normalizeFixed(rawBefore, field)

      // Shared parse helper
      const parseNum = (val) => {
        const s = String(val ?? '')
          .trim()
          .replace(/,/g, '')
        const n = Number(s)
        return Number.isFinite(n) ? n : 0
      }

      // Helper: get current quantity_unit for this row
      const getCurrentQuantityUnit = () =>
        parseNum(
          distributionManager.getFixedField(
            hinmeiCode,
            niukabango,
            'quantity_unit',
            item.quantity_unit
          )
        )

      // Helper: get current quantity for this row
      const getCurrentQuantity = () =>
        parseNum(
          distributionManager.getFixedField(
            hinmeiCode,
            niukabango,
            'quantity',
            item.quantity ?? item.total_quantity ?? item.totalQuantity ?? 0
          )
        )

      // Validation for quantity_unit (入数) field
      if (field === 'quantity_unit') {
        const newQtyUnit = parseNum(value)
        if (newQtyUnit > 0) {
          const currentQty = getCurrentQuantity()
          if (currentQty > 0 && currentQty % newQtyUnit !== 0) {
            alert(
              `数量 (${currentQty}) は 入数 (${newQtyUnit}) で割り切れません。\n入数を再入力してください。`
            )
            // Revert to previous value
            if (hadFixedField) {
              distributionManager.updateFixedField(
                hinmeiCode,
                niukabango,
                field,
                beforeValue,
                true
              )
            } else {
              distributionManager.clearFixedField(
                hinmeiCode,
                niukabango,
                field,
                true
              )
            }
            return
          }
        }
      }

      // Validation for quantity field
      if (field === 'quantity') {
        const newQty = parseNum(value)
        const originalQty = parseNum(
          item.quantity ?? item.total_quantity ?? item.totalQuantity ?? 0
        )

        if (newQty > 0 && newQty > originalQty) {
          alert(`数量は元の値 (${originalQty}) を超えることはできません。`)
          if (hadFixedField) {
            distributionManager.updateFixedField(
              hinmeiCode,
              niukabango,
              field,
              beforeValue,
              true
            )
          } else {
            distributionManager.clearFixedField(
              hinmeiCode,
              niukabango,
              field,
              true
            )
          }
          return
        }

        // If 入数 (quantity_unit) is set, check divisibility
        const currentQtyUnit = getCurrentQuantityUnit()
        if (newQty > 0 && currentQtyUnit > 0 && newQty % currentQtyUnit !== 0) {
          alert(
            `数量 (${newQty}) は 入数 (${currentQtyUnit}) で割り切れません。\n数量を再入力してください。`
          )
          if (hadFixedField) {
            distributionManager.updateFixedField(
              hinmeiCode,
              niukabango,
              field,
              beforeValue,
              true
            )
          } else {
            distributionManager.clearFixedField(
              hinmeiCode,
              niukabango,
              field,
              true
            )
          }
          return
        }

        if (newQty === originalQty) {
          // Allow resetting to original, but don't add log entry
          distributionManager.clearFixedField(
            hinmeiCode,
            niukabango,
            field,
            true
          )
          return
        }
      }

      distributionManager.updateFixedField(
        hinmeiCode,
        niukabango,
        field,
        value,
        true
      )

      const fieldLabel = fieldMap[field] || ''
      const baseNiukabango = String(niukabango).split('__')[0]
      distributionManager.addLogEntry({
        action: `[${distributionManager.username}] ${hinmeiCode || '（新規行）'} - ${baseNiukabango} の ${fieldLabel} を ${value} に変更しました`,
        changeData: {
          hinmeiCode,
          niukabango,
          field,
          type: 'fixed',
          originalValue: beforeValue,
          newValue: value,
          changed: true
        },
        up: () => {
          distributionManager.updateFixedField(
            hinmeiCode,
            niukabango,
            field,
            value,
            true
          )
        },
        down: () => {
          distributionManager.updateFixedField(
            hinmeiCode,
            niukabango,
            field,
            beforeValue,
            true
          )
        }
      })
    },
    [distributionManager, isReadOnly]
  )

  const [layoutRevision, setLayoutRevision] = useState(0)
  useEffect(() => {
    const handleUpdate = () => setLayoutRevision((revision) => revision + 1)
    const unsubReorder = eventBus.on(EVENTS.COLUMN_REORDERED, handleUpdate)
    const unsubAdded = eventBus.on(EVENTS.COLUMN_ADDED, handleUpdate)
    const unsubRemoved = eventBus.on(EVENTS.COLUMN_REMOVED, handleUpdate)
    const unsubResized = eventBus.on(EVENTS.COLUMN_RESIZED, handleUpdate)
    const unsubCustomerHeader = eventBus.on(
      EVENTS.CUSTOMER_HEADER_CHANGED,
      handleUpdate
    )
    const unsubDistributionChanged = eventBus.on(
      EVENTS.DISTRIBUTION_CHANGED,
      handleUpdate
    )
    const unsubDistributionCommitted = eventBus.on(
      EVENTS.DISTRIBUTION_COMMITTED,
      handleUpdate
    )
    const unsubCellValueChanged = eventBus.on(
      EVENTS.CELL_VALUE_CHANGED,
      handleUpdate
    )

    return () => {
      unsubReorder()
      unsubAdded()
      unsubRemoved()
      unsubResized()
      unsubCustomerHeader()
      unsubDistributionChanged()
      unsubDistributionCommitted()
      unsubCellValueChanged()
    }
  }, [])

  useEffect(() => {
    const unsubFocused = eventBus.on(EVENTS.CELL_FOCUSED, () => {
      clearSelection()
    })
    return () => unsubFocused()
  }, [clearSelection])

  useEffect(() => {
    clearSelection()
    closeContextMenu()
  }, [farmFilterValues, clearSelection, closeContextMenu])

  const allColumns = columnManager.getColumns()
  const columns = allColumns.filter((col) => {
    if (col.type === 'customer' || col.type === 'black-circle') {
      const belongsToAnyItem = allItems.some((item) =>
        distributionManager.hasCustomerInItem(item, col.customerCode)
      )
      if (!belongsToAnyItem) return false
      return !hiddenColumnKeys.has(col.customerCode)
    }
    return true
  })

  const baseColumns = columns.filter(
    (col) => col.type !== 'customer' && col.type !== 'black-circle'
  )
  const customerColumns = columns.filter(
    (col) => col.type === 'customer' || col.type === 'black-circle'
  )
  const totalColumnCount = columns.length
  const [buyerLeft, setBuyerLeft] = useState(0)
  const getStickyLeft = useCallback(
    (columnIndex) => {
      let left = 0
      for (let i = 0; i < columnIndex; i++) {
        left += columns[i].width
      }
      return left
    },
    [columns]
  )

  // 買受人 is anchored to the rendered right edge of 利益. This keeps the
  // sticky columns aligned even when content or manual resizing changes the
  // actual table layout beyond the configured widths.
  useLayoutEffect(() => {
    const scrollContainer = tableScrollRef.current
    const table = scrollContainer?.querySelector('.rp-table')
    if (!table) return undefined

    const benefitHeader = table.querySelector(
      'th[data-column-id="benefit"]'
    )
    if (!benefitHeader) return undefined

    let frameId = null
    const updateBuyerLeft = () => {
      frameId = null
      const containerRect = scrollContainer.getBoundingClientRect()
      const nextBuyerLeft =
        benefitHeader.getBoundingClientRect().right -
        containerRect.left -
        scrollContainer.clientLeft
      setBuyerLeft((current) =>
        current === nextBuyerLeft ? current : nextBuyerLeft
      )
    }

    const scheduleUpdate = () => {
      if (frameId === null) {
        frameId = requestAnimationFrame(updateBuyerLeft)
      }
    }

    updateBuyerLeft()
    const observer = new ResizeObserver(scheduleUpdate)
    observer.observe(table)
    observer.observe(benefitHeader)

    return () => {
      observer.disconnect()
      if (frameId !== null) cancelAnimationFrame(frameId)
    }
  }, [columns, layoutRevision, tableScrollRef])

  // Keep the table header visible when an active filter produces no rows.
  // Only show the empty state when there is no source data at all.
  if (selectedItems.length === 0 && allItems.length === 0) {
    return <EmptyState />
  }

  return (
    <div className='rp-table-container' aria-busy={isFilterLoading}>
      <div
        ref={tableScrollRef}
        className='rp-table-scroll'
        onScroll={captureScrollPosition}
        onClick={(e) => {
          if (
            !e.target.closest('th.customer-header') &&
            !e.target.closest('tr[data-row-idx]')
          ) {
            clearSelection()
          }
        }}
        onDragOver={(e) => {
          if (isReadOnly) return
          handleDragOver(e, null)
          if (!rowReorderDisabled) handleRowDragOver(e, null)
        }}
        onDrop={(e) => {
          if (isReadOnly) return

          handleDrop(e, null)
          if (!rowReorderDisabled) handleRowDrop(e, null)
        }}
      >
        <table className='rp-table excel'>
          <TableHeader
            columns={columns}
            allColumns={allColumns}
            hiddenColumnKeys={hiddenColumnKeys}
            hiddenRowKeys={hiddenRowKeys}
            hiddenHeaderFieldKeys={hiddenHeaderFieldKeys}
            onUnhideItem={onUnhideItem}
            baseColumns={baseColumns}
            customerColumns={customerColumns}
            totalColumnCount={totalColumnCount}
            columnManager={columnManager}
            todayString={todayString}
            getStickyLeft={getStickyLeft}
            buyerLeft={buyerLeft}
            selectedColumn={selectedColumn}
            selectedColumnIds={selectedColumnIds}
            setSelectedColumn={setSelectedColumn}
            setSelectedRow={setSelectedRow}
            handleColumnContextMenu={handleColumnContextMenu}
            handleColumnSelection={handleColumnSelection}
            handleHeaderRowContextMenu={handleHeaderRowContextMenu}
            handleHeaderFieldSelection={handleHeaderFieldSelection}
            selectedHeaderFieldKeys={selectedHeaderFieldKeys}
            handleMouseDown={handleMouseDown}
            newColumnId={newColumnId}
            newColumnInputRefs={newColumnInputRefs}
            hasAutoFocused={hasAutoFocused}
            handleCustomerCodeChange={handleCustomerCodeChange}
            handleCustomerCodeEmptyLabel={handleCustomerCodeEmptyLabel}
            handleAddNewColumn={handleAddNewColumn}
            handleHeaderFieldKeyDown={handleHeaderFieldKeyDown}
            handleHeaderFieldKeyUp={handleHeaderFieldKeyUp}
            handleHeaderFieldBlur={handleHeaderFieldBlur}
            handleHeaderFieldToggle={handleHeaderFieldToggle}
            isDisabled={isDisabled}
            selectedItems={allItems}
            distributionManager={distributionManager}
            orders={orders}
            productName={productName}
            saleMan={saleMan}
            hasOriginalOrderQuantity={hasOriginalOrderQuantity}
            editingColumnId={editingColumnId}
            setEditingColumnId={setEditingColumnId}
            handleDragStart={handleDragStart}
            handleDragEnd={handleDragEnd}
            handleDragOver={handleDragOver}
            handleDragLeave={handleDragLeave}
            handleDrop={handleDrop}
            draggedColumnId={draggedColumnId}
            dropTargetId={dropTargetId}
            isAtEnd={isAtEnd}
            handleRowDragOver={handleRowDragOver}
            draggedRowIdx={draggedRowIdx}
            rowReorderDisabled={rowReorderDisabled}
            gradeFilterValues={gradeFilterValues}
            sizeFilterValues={sizeFilterValues}
            gradeFilterOptions={gradeFilterOptions}
            sizeFilterOptions={sizeFilterOptions}
            onGradeFilterChange={onGradeFilterChange}
            onSizeFilterChange={onSizeFilterChange}
            isFilterLoading={isFilterLoading}
            isReadOnly={isReadOnly}
          />

          <TableBody
            selectedItems={selectedItems}
            customerColumns={customerColumns}
            selectedColumn={selectedColumn}
            selectedColumnIds={selectedColumnIds}
            selectedRowIndexes={selectedRowIndexes}
            handleRowSelection={handleRowSelection}
            distributionManager={distributionManager}
            getStickyLeft={getStickyLeft}
            buyerLeft={buyerLeft}
            showTooltip={showTooltip}
            setShowTooltip={setShowTooltip}
            handleFixedCellChange={handleFixedCellChange}
            dropTargetId={dropTargetId}
            isAtEnd={isAtEnd}
            handleRowDragStart={handleRowDragStart}
            handleRowDragEnd={handleRowDragEnd}
            handleRowDragOver={handleRowDragOver}
            handleRowDrop={handleRowDrop}
            draggedRowIdx={draggedRowIdx}
            draggedRowKey={draggedRowKey}
            dropTargetRowIdx={dropTargetRowIdx}
            handleDragOver={handleDragOver}
            handleDrop={handleDrop}
            hiddenRowKeys={hiddenRowKeys}
            onUnhideItem={onUnhideItem}
            handleRowContextMenu={handleRowContextMenu}
            onAddPrice={onAddPrice}
            onDeletePrice={onDeletePrice}
            gradeOptions={gradeOptions}
            sizeOptions={sizeOptions}
            rowReorderDisabled={rowReorderDisabled}
            isReadOnly={isReadOnly}
          />
        </table>
      </div>

      {isFilterLoading && (
        <div className='rp-table-filter-loading' role='status' aria-live='polite'>
          <span className='rp-table-filter-spinner' aria-hidden='true' />
          <span>フィルター中...</span>
        </div>
      )}

      {!isReadOnly && (
        <ContextMenuOverlay
          contextMenu={contextMenu}
          distributionManager={distributionManager}
          closeContextMenu={closeContextMenu}
          handleCopyRow={handleCopyRow}
          handlePasteRow={handlePasteRow}
          handleCopyColumn={handleCopyColumn}
          handlePasteColumn={handlePasteColumn}
          handleInsertColumnLeft={handleInsertColumnLeft}
          handleInsertColumnRight={handleInsertColumnRight}
          handleDeleteColumn={handleDeleteColumn}
          hasOriginalOrderQuantity={hasOriginalOrderQuantity}
          handleHideRow={handleHideRow}
          handleHideHeaderField={handleHideHeaderField}
          handleHideColumn={handleHideColumn}
          orders={orders}
          selectedItems={selectedItems}
          columns={columns}
          onAddPrice={onAddPrice}
          onDeletePrice={onDeletePrice}
        />
      )}
    </div>
  )
}

export default React.memo(DistributionTable)
